
import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { isPhantomInAppBrowser } from '../utils/phantomUtils';

/**
 * Hook that manages wallet selection and connection state
 */
export const useWalletState = () => {
  const { 
    wallet,
    publicKey, 
    connected,
    wallets,
    select
  } = useWallet();
  
  const [balance, setBalance] = useState<number | null>(null);
  const [isSelectingWallet, setIsSelectingWallet] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);

  // Auto-select Phantom if it's available and no wallet is selected
  useEffect(() => {
    if (!wallet && wallets.length > 0) {
      const phantomWallet = wallets.find(w => w.adapter.name === 'Phantom');
      if (phantomWallet) {
        select(phantomWallet.adapter.name);
      }
    }
  }, [wallet, wallets, select]);

  // Check if already connected in Phantom browser
  useEffect(() => {
    const checkExistingConnection = async () => {
      if (isPhantomInAppBrowser() && window?.solana?.isConnected) {
        try {
          console.log("Detected existing Phantom connection, reconnecting...");
          // For Phantom in-app, select and connect
          const phantomWallet = wallets.find(w => w.adapter.name === 'Phantom');
          if (phantomWallet && !connected) {
            select(phantomWallet.adapter.name);
          }
        } catch (error) {
          console.error("Error with auto-reconnect:", error);
        }
      }
    };
    
    checkExistingConnection();
  }, [wallets, select, connected]);

  // Reset connection attempts when wallet changes
  useEffect(() => {
    setConnectionAttempts(0);
  }, [wallet]);

  return {
    wallet,
    wallets,
    publicKey,
    connected,
    balance,
    setBalance,
    isSelectingWallet,
    setIsSelectingWallet,
    connectionAttempts,
    setConnectionAttempts,
    select
  };
};
